// Confirmación modal con <dialog> nativo: el navegador atrapa el foco, cierra con Escape y lo pone por
// encima de todo (capa superior), igual en todos los sistemas. El foco inicial va a «Cancelar».
import { type ReactNode, useEffect, useId, useRef } from 'react';
import styles from './ConfirmDialog.module.css';

/** `form`: encabezado a la izquierda (icono, título y subtítulo) para diálogos con campos o listas. */
export function ConfirmDialog({
  open, icon, title, subtitle, variant = 'confirm', children, confirmLabel, busyLabel, busy = false, confirmDisabled = false, hideCancel = false, cancelLabel = 'Cancelar',
  formId, onConfirm, onCancel,
}: {
  open: boolean;
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  variant?: 'confirm' | 'form';
  confirmDisabled?: boolean;
  /** Solo un botón (avisos que se cierran con «Listo»). */
  hideCancel?: boolean;
  /** Texto del botón secundario (por defecto «Cancelar»). */
  cancelLabel?: string;
  /** El botón principal envía ese formulario (Enter también): onConfirm no se llama. */
  formId?: string;
  children?: ReactNode;
  confirmLabel: string;
  busyLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog ref={dialog} className={styles.dialog} aria-labelledby={titleId} aria-describedby={children ? bodyId : undefined}
      // Escape: el navegador dispara «cancel»; se cierra desde el estado para no desincronizarse.
      onCancel={(event) => { event.preventDefault(); if (!busy) onCancel(); }}
      // Pulsar fuera de la tarjeta (sobre el fondo) también cancela.
      onClick={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
      <div className={styles.card} data-variant={variant}>
        <div className={styles.head}>
          {icon && <span className={styles.icon} aria-hidden="true">{icon}</span>}
          <div className={styles.headText}>
            <h2 id={titleId} className={styles.title}>{title}</h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
        </div>
        {children && <div id={bodyId} className={styles.body}>{children}</div>}
        <div className={styles.actions}>
          {!hideCancel && <button type="button" className={`btn ${styles.cancel}`} autoFocus disabled={busy} onClick={onCancel}>{cancelLabel}</button>}
          <button type={formId ? 'submit' : 'button'} form={formId} className="btn btn-primary" autoFocus={hideCancel}
            disabled={busy || confirmDisabled} aria-busy={busy} onClick={formId ? undefined : onConfirm}>
            {busy ? busyLabel ?? confirmLabel : confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  );
}
